import type { TypographyBreakpoint } from "@onestore/hel/dist/components/atoms/TextAtm/TextAtm.props"
import type Theme from "@onestore/hel/dist/typings/theme"
import type { TextProps } from "@ionos-web-design-system/react"

export type UdsVariant = NonNullable<TextProps["variant"]>
export type UdsWeight = NonNullable<TextProps["weight"]>
export type UdsColor = NonNullable<TextProps["color"]>

export interface UdsTypographyProps {
  variant?: UdsVariant
  weight?: UdsWeight
}

const helToUdsMap: Partial<Record<Theme.Typography, UdsTypographyProps>> = {
  // 11px
  nano1: { variant: "bodyXs" },
  nano2: { variant: "bodyXs" },
  nano2_u: { variant: "bodyXs" },
  nano3: { variant: "bodyXs", weight: "bold" },
  // 12px
  tiny1: { variant: "bodyXs" },
  tiny1_u: { variant: "bodyXs" },
  tiny2: { variant: "bodyXs", weight: "black" },
  tiny3: { variant: "bodyXs", weight: "black" },
  // 13px
  xsmall1: { variant: "bodySm" },
  xsmall1_h: { variant: "bodySm" },
  xsmall2: { variant: "bodySm" },
  xsmall2_u: { variant: "bodySm" },
  xsmall3: { variant: "bodySm", weight: "bold" },
  // 14px
  small1: { variant: "bodySm" },
  small2: { variant: "bodySm" },
  small3: { variant: "bodySm", weight: "black" },
  small4: { variant: "bodySm", weight: "bold" },
  small4_u: { variant: "bodySm", weight: "bold" },
  // 16px
  medium1_s: { variant: "body" },
  medium1_h: { variant: "body" },
  medium2_s: { variant: "body" },
  medium2_h: { variant: "body" },
  medium3_s: { variant: "body", weight: "bold" },
  medium3_h: { variant: "body", weight: "bold" },
  medium3_h_semi: { variant: "body", weight: "bold" },
  medium4_s: { variant: "body", weight: "bold" },
  medium4_h: { variant: "body", weight: "bold" },
  // 18px
  big1: { variant: "bodyLg" },
  big2: { variant: "bodyLg" },
  big3: { variant: "bodyLg", weight: "black" },
  big4: { variant: "bodyLg", weight: "black" },
  // 24px desktop (20px mobile)
  large1: { variant: "heading2xl" },
  large2: { variant: "heading2xl" },
  large3: { variant: "heading2xl", weight: "bold" },
  // 28px → closest is heading3xl (30px desktop)
  xlarge: { variant: "heading4xl", weight: "black" },
  // 32px → heading3xl (30px desktop)
  great: { variant: "heading3xl" },
  // 36px → heading4xl (36px, no responsive)
  huge: { variant: "heading4xl" },
  // 40px → heading5xl (48px desktop) – closest above
  xhuge: { variant: "heading5xl", weight: "bold" },
  // 44px → heading5xl (48px desktop)
  sgiant: { variant: "heading5xl" },
  sgiant_2: { variant: "heading5xl" },
  lgiant: { variant: "heading5xl" },
  // 48px → heading5xl (48px desktop)
  giant: { variant: "heading6xl" },
  giant2: { variant: "heading6xl", weight: "bold" },
  // 48px → heading5xl (48px desktop)
  titleFig: { variant: "heading5xl" },
  // buttons
  button: { variant: "body" },
  buttonBig: { variant: "bodyLg" },
  buttonSmall: { variant: "bodySm" },
}

/**
 * Maps a hel Typography token (or responsive TypographyBreakpoint) to UDS Text
 * variant/weight props. For TypographyBreakpoint the `medium` breakpoint is
 * used with fallback to `small`.
 */
export function helTypographyToUdsProps(
  typography: Theme.Typography | TypographyBreakpoint | undefined
): UdsTypographyProps {
  if (!typography) return { variant: "body" }

  if (typeof typography === "string") {
    return helToUdsMap[typography] ?? { variant: "body" }
  }

  // TypographyBreakpoint – use medium breakpoint, fallback to small
  const token = typography.medium ?? typography.small
  return helToUdsMap[token] ?? { variant: "body" }
}

/**
 * Maps a hel Emphasis value to UDS Text `color` prop.
 * Returns `undefined` for the default (full-opacity) case so that the UDS
 * component can apply its own default color.
 */
export function helEmphasisToUdsColor(
  emphasis: Theme.Emphasis | undefined
): UdsColor | undefined {
  switch (emphasis) {
    case "medium":
    case "half":
      return "muted"
    case "low":
    case "extraLow":
      return "subtle"
    case "high":
    default:
      return undefined
  }
}
